<template>
  <v-dialog
    v-model="dialog"
    v-bind="$attrs"
    :persistent="persistent || loading || fullscreenCondition"
    :no-click-animation="fullscreenCondition"
    :max-width="maxWidth"
    width="100%"
    role="dialog"
    :content-class="fullscreenCondition ? 'workaround-fullscreen-v-dialog' : ''"
  >
    <v-card
      :class="`px-${px} py-2`"
      :data-test="dataTest"
      :tile="fullscreenCondition"
      :style="cardStyle"
    >
      <div :class="contentPaddingX ? `px-${contentPaddingX}` : undefined">
        <div
          :class="{
            'py-2': $vuetify.breakpoint.mdAndUp,
            'py-5': $vuetify.breakpoint.smAndDown
          }"
        >
          <template v-if="customHeader">
            <slot name="header" />
          </template>
          <h1 v-else style="font-size: 24px">
            <v-row class="mx-0" dense>
              <v-col cols="9" class="ma-auto">
                <div class="smaller-mobile">
                  <slot name="header" />
                </div>
                <small class="muted--text d-block" style="font-size: 14px">
                  <slot name="subheader" />
                </small>
              </v-col>

              <v-col cols="3" class="text-right">
                <v-btn
                  icon
                  :disabled="loading"
                  class="mx-0"
                  data-test="closeDialog"
                  :aria-label="$t('common:closeButton.alt')"
                  @click="[$emit('input', false), $emit('close')]"
                >
                  <v-icon>$iconTimesCircle</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </h1>
        </div>

        <div>
          <slot name="hint" />
        </div>

        <section>
          <AppShowFeedback
            ref="dialogFeedback"
            in-dialog
            full-width
            class="mb-4"
          />
          <slot />
        </section>
        <section v-if="!hideActions">
          <div :class="[!noParentCssActions ? '' : '', 'py-1']">
            <div v-if="!$slots.actions" class="mt-4">
              <template v-if="$vuetify.breakpoint.mdAndUp">
                <v-row
                  dense
                  :class="[actionsClass]"
                  style="justify-content: flex-end"
                >
                  <div class="mb-1 text-right">
                    <v-btn
                      :class="[$vuetify.breakpoint.smAndDown ? 'my-2' : '']"
                      outlined
                      rounded
                      min-width="185px"
                      :block="$vuetify.breakpoint.smAndDown"
                      :disabled="loading"
                      data-test="dialogClose"
                      @click.native="$emit('input', false)"
                    >
                      <slot name="cancel">
                        {{
                          $t("common:button.cancel", "Cancel", "Cancel button")
                        }}
                      </slot>
                    </v-btn>
                  </div>
                  <div class="hidden-md-and-up py-3" />
                  <div v-if="!onlyCancelAction" class="mb-1 ml-2 text-right">
                    <v-tooltip bottom :disabled="!submitTooltip">
                      <template #activator="{ on, attrs }">
                        <v-btn
                          :class="[
                            $vuetify.breakpoint.smAndDown ? 'my-2 mb-10' : ''
                          ]"
                          color="accent"
                          data-test="dialogSubmit"
                          min-width="185px"
                          :loading="loading"
                          rounded
                          :block="$vuetify.breakpoint.smAndDown"
                          :disabled="loading || disabledSubmit"
                          v-bind="attrs"
                          v-on="on"
                          @click.native="$emit('submit-form', $event)"
                        >
                          <slot name="submit">
                            {{
                              $t(
                                "common:button.confirm",
                                "Confirm",
                                "Confirm button"
                              )
                            }}
                          </slot>
                        </v-btn>
                      </template>
                      <span>{{ submitTooltip }}</span>
                    </v-tooltip>
                  </div>
                </v-row>
              </template>
              <template v-else>
                <div style="width: 100%">
                  <v-btn
                    :class="[$vuetify.breakpoint.smAndDown ? 'my-2' : '']"
                    outlined
                    :disabled="loading"
                    rounded
                    block
                    data-test="dialogClose"
                    @click.native="$emit('input', false)"
                  >
                    <slot name="cancel">
                      {{
                        $t("common:button.cancel", "Cancel", "Cancel button")
                      }}
                    </slot>
                  </v-btn>

                  <div class="hidden-md-and-up py-2" />

                  <v-tooltip
                    v-if="!onlyCancelAction"
                    bottom
                    :disabled="!submitTooltip"
                    class="d-block"
                  >
                    <template #activator="{ on, attrs }">
                      <v-btn
                        :class="[
                          $vuetify.breakpoint.smAndDown ? 'my-2 mb-10' : ''
                        ]"
                        color="accent"
                        data-test="dialogSubmit"
                        :loading="loading"
                        :disabled="loading || disabledSubmit"
                        rounded
                        block
                        v-bind="attrs"
                        v-on="on"
                        @click.native="$emit('submit-form', $event)"
                      >
                        <slot name="submit">
                          {{
                            $t(
                              "common:button.confirm",
                              "Confirm",
                              "Confirm button"
                            )
                          }}
                        </slot>
                      </v-btn>
                    </template>
                    <span>{{ submitTooltip }}</span>
                  </v-tooltip>
                </div>
              </template>
            </div>

            <div v-else class="mt-4 justify-end">
              <slot name="actions"></slot>
            </div>
          </div>

          <div class="hidden-md-and-up pb-10" />
          <div class="hidden-md-and-up pb-10" />
        </section>

        <div v-else class="pb-2" />
      </div>
    </v-card>
  </v-dialog>
</template>
<script>
import { defineComponent } from "vue";
import AppShowFeedback from "@AppShows/AppShowFeedback.vue";
export default defineComponent({
  name: "AppDialogContent",
  components: {
    AppShowFeedback
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    submitTooltip: {
      type: String,
      default: null
    },
    maxWidth: {
      type: String,
      default: "675px"
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    customHeader: {
      type: Boolean,
      default: false
    },
    persistent: {
      type: Boolean,
      default: false
    },
    dataTest: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabledSubmit: {
      type: Boolean,
      default: false
    },
    error: {
      type: [Object, Error, Array],
      default: () => null
    },
    noFullscreen: {
      type: Boolean,
      default: false
    },
    forceFullscreen: {
      type: Boolean,
      default: false
    },
    onlyCancelAction: {
      type: Boolean,
      default: false
    },
    noParentCssActions: {
      type: Boolean,
      default: false
    },
    px: {
      type: [String, Number],
      default: 6
    },
    contentPaddingX: {
      type: [String, Number],
      default: 2
    },
    disableHashHistory: {
      type: Boolean,
      default: false
    },
    cardStyle: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      oldHash: null,
      oldYScrollPosition: null
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      }
    },
    actionsClass() {
      return [
        this.$vuetify.breakpoint.smAndUp ? "px-3" : "px-2",
        this.$vuetify.breakpoint.mdAndUp ? "text-right" : ""
      ];
    },
    fullscreenCondition() {
      return (
        this.forceFullscreen ||
        (!this.noFullscreen && this.$vuetify.breakpoint.smAndDown)
      );
    }
  },
  watch: {
    dialog(newState, oldState) {
      if (this.disableHashHistory) return;

      this.oldHash = location.hash;
      this.oldYScrollPosition = window.scrollY;

      // newState is true value and ol is false value
      if (newState && !oldState) {
        if (!location.hash) {
          history.pushState(null, "fipa", "#dialog");
        } else if (location.hash != "#dialog") {
          location.hash = "#dialog";
        }
        window.onpopstate = () => {
          this.dialog = !this.dialog;
        };
      } else if (newState == false) {
        window.onpopstate = () => ({});
        if (location.hash == "#dialog") {
          if (this.oldHash && this.oldHash != "") {
            // If URL already had hash before replaced to #dialog, then doing history back may trigger scroll to elsewhere where its supposed to.
            // This one time event listener forces to keep in Y scroll position as it was before dialog opened
            window.addEventListener(
              "hashchange",
              () => {
                window.scrollTo({
                  top: this.oldYScrollPosition,
                  left: 0,
                  behavior: "auto"
                });
              },
              {
                once: true
              }
            );
          }
          this.$router.go(-1);
        }
      }
    },
    error: {
      deep: true,
      handler(newVal) {
        if (!this.$refs.dialogFeedback) return;

        if (newVal === null || Object.keys(newVal).length == 0) {
          this.$refs.dialogFeedback.close();
          return;
        }
        if (newVal.type === "graphql") {
          this.$refs.dialogFeedback.showGraphqlError(newVal.err);
        } /* else if (Object.keys(newVal).length > 0) {
           this.$refs.dialogFeedback.open("warn", {
              messages: newVal,
              text: "Please review inputs"
            });
        }*/
      }
    }
  },
  destroyed() {
    window.onpopstate = () => ({});
  },
  methods: {
    openFeedback() {
      this.$refs.dialogFeedback.open("warn", {
        text: this.$t("common:reviewInput.text", "Please review inputs")
      });
    },
    closeFeedback() {
      this.$refs.dialogFeedback.close();
    }
  }
});
</script>
<style lang="scss" scoped>
@media (max-width: 768px) {
  .smaller-mobile {
    font-size: 75%;
  }
}
</style>
<style lang="scss">
.workaround-fullscreen-v-dialog.v-dialog {
  margin: 0 !important;
  border-radius: 0px !important;
  &:not(.v-dialog--fullscreen) {
    max-height: 100% !important;
  }
  & > .v-card {
    min-height: 100vh !important;
  }
}
</style>
